import React from 'react';
import { LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import {
  FiFileText, FiBriefcase, FiClock, FiCalendar, FiXCircle, FiTrendingUp,
} from 'react-icons/fi';
import StatTile from '../shared/StatTile';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} MT`;

// Thin wrapper that keeps the drill-down click behavior while the tile
// visuals live in the shared StatTile.
const ClickableTile = ({ onClick, children }) => {
  if (!onClick) return <div className="tw-h-full">{children}</div>;
  return (
    <div
      onClick={onClick}
      className="tw-h-full tw-rounded tw-cursor-pointer tw-transition-all hover:tw-shadow-md hover:-tw-translate-y-0.5"
    >
      {children}
    </div>
  );
};
ClickableTile.propTypes = { onClick: PropTypes.func, children: PropTypes.node };

// Each KPI drills down to its matching record subset via
// onDrillDown(title, extraParams) — params mirror the backend filter set.
const QuotationSummaryCards = ({ data, loading, onDrillDown }) => {
  if (loading && !data) {
    return (
      <div className="tw-bg-white tw-rounded tw-border tw-border-gray-100 tw-flex tw-justify-center tw-py-10">
        <LoadingSpinner size="md" />
      </div>
    );
  }
  if (!data) return null;

  const drill = (title, extraParams) =>
    onDrillDown ? () => onDrillDown(title, extraParams) : undefined;

  const wowDelta = (data.this_week || 0) - (data.last_week || 0);

  return (
    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-3">
      <ClickableTile onClick={drill('All Quotations', {})}>
        <StatTile label="Total Quotations" value={fmt(data.total)}
          sub={fmtW(data.total_weight)}
          icon={FiFileText} theme="indigo" />
      </ClickableTile>
      <StatTile label="Prospective" value={fmt(data.prospective)}
        sub="P-numbered (negotiation)"
        icon={FiTrendingUp} theme="cyan" />
      <StatTile label="Became Job" value={fmt(data.became_job)}
        sub={`${data.became_job_rate || 0}% conversion`}
        icon={FiBriefcase} theme="green" />
      <StatTile label="Lost" value={fmt(data.lost)}
        sub="Quotations marked lost"
        icon={FiXCircle} theme="rose" />
      <StatTile label="This Week" value={fmt(data.this_week)}
        sub={`${wowDelta >= 0 ? '+' : ''}${fmt(wowDelta)} vs last week`}
        icon={FiCalendar} theme="violet" />
      <StatTile label="Last Week" value={fmt(data.last_week)}
        sub="Previous ISO week"
        icon={FiClock} theme="amber" />
    </div>
  );
};

QuotationSummaryCards.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  onDrillDown: PropTypes.func,
};
QuotationSummaryCards.defaultProps = { data: null, loading: false, onDrillDown: null };

export default QuotationSummaryCards;
